import React, { useEffect, useContext } from 'react';
import {
  Check as CheckIcon,
  Folder as FolderIcon,
  Delete as DeleteIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  InsertDriveFile as InsertDriveFileIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon
} from '@material-ui/icons';

import * as Styled from './DocumentFileExplorer.styled';
import { FileDto } from '../Document.interface';
import { useFindFolders } from '../Document.hook';
import { filterFileTree } from './DocumentFileExplorer';
import { FileExplorerContext } from './FileExplorer.context';

const expandTrashIcon = (
  <>
    <ChevronRightIcon />
    <DeleteIcon />
  </>
);
const collapseTrashIcon = (
  <>
    <ExpandMoreIcon />
    <DeleteOutlinedIcon />
  </>
);

const fileIcon = <InsertDriveFileIcon style={{ marginLeft: 18 }} />;
const selectedFileIcon = (
  <>
    <CheckIcon />
    <InsertDriveFileOutlinedIcon />
  </>
);

const getEndIcon = (
  hasZeroChildren: boolean,
  isTrash: boolean = false
): JSX.Element | undefined => {
  if (!(hasZeroChildren || isTrash)) {
    return undefined;
  }

  return (
    <>
      <ChevronRightIcon color={hasZeroChildren ? 'disabled' : undefined} />
      {isTrash ? <DeleteIcon /> : <FolderIcon />}
    </>
  );
};

export interface DocumentFileExplorerItemProps {
  file: FileDto;
  selected?: boolean;
  hideTrashDir?: boolean;
  includeFiles?: boolean;
  useContextValues?: boolean;
  renderTree?: (node: FileDto) => void;
  onSelectNode?: (nodeId: string, file: FileDto) => void;
  setFileChildren?: (file: FileDto, children: FileDto[]) => void;
}

const DocumentFileExplorerItem: React.FC<DocumentFileExplorerItemProps> = (
  props: DocumentFileExplorerItemProps
) => {
  const {
    file,
    selected,
    hideTrashDir,
    includeFiles,
    useContextValues,
    renderTree,
    onSelectNode,
    setFileChildren
  } = props;
  const { setTreeNodeFetcher } = useContext(FileExplorerContext);
  const [{ data, isLoading, error }, get] = useFindFolders(!includeFiles);

  // save get fetch request to context if using context values
  useContextValues && setTreeNodeFetcher(`${file.id}`, get);

  const hasChildren = !!file.children?.length;
  const hasZeroChildren = file.children?.length === 0;

  const nodeId = `${file.id}`;
  const label = file.name + (isLoading && file.isFolder ? ' Loading...' : '');
  const isTrash = file.name === 'Trash' && file.isLocked;

  // use disabled color when there are 0 children fetched
  const endIcon = file.isFolder
    ? getEndIcon(hasZeroChildren, isTrash)
    : selected
    ? selectedFileIcon
    : fileIcon;

  useEffect(() => {
    if (data && !error) {
      setFileChildren?.(file, (data as FileDto).children || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, file]);

  return (
    <Styled.TreeItem
      {...{ nodeId, label, endIcon }}
      className={selected ? 'selected' : undefined}
      expandIcon={isTrash ? expandTrashIcon : undefined}
      collapseIcon={isTrash ? collapseTrashIcon : undefined}
      onClick={() => {
        file.isFolder && !hasChildren && get(file.id);
        onSelectNode?.(nodeId, file);
      }}
    >
      {Array.isArray(file.children)
        ? filterFileTree(file.children, hideTrashDir).map(
            node => renderTree?.(node) || null
          )
        : null}
    </Styled.TreeItem>
  );
};

export default DocumentFileExplorerItem;
